import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import GenericTabView from "./admin modules/GenericTabView";
import ChapitreDetailsDialog from "./admin modules/ChapitreDetailsDialog";
import ChapitreEditDialog from "./admin modules/ChapitreEditDialog";
import UserListTable from "./admin modules/UserListTable";
import {
  allowedStatusTransitions,
  getUserStatus,
  isConfirmedLike,
  isWaiting,
  normalizeID,
  normalizeWaitList,
  statusOptions
} from "./admin modules/dashboardStatus";
import countries from '../data/country.json'
import OccupancyRate from "../front modules/OccupancyRate";
import formlabels from '../data/formlabels.json'

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setSavingUserIDs] = useState({});
  const [chapitreDialogVisible, setChapitreDialogVisible] = useState(false);
  const [activeChapitre, setActiveChapitre] = useState(null);
  const [chapitreEditVisible, setChapitreEditVisible] = useState(false);
  const [chapitreEditTarget, setChapitreEditTarget] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {

    const fetchData = async () => {
      try {
        const response = await fetch("./backend/admin/adminDataJson.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        const jsonData = await response.json();
        console.log("✅ Server validation response:", jsonData);
        //TODO: clean error handling here
        if (jsonData.error) {
          console.error("❌ Invalid session, redirecting to login...");
          localStorage.clear();
          navigate("./login");
        } else {
          setData(jsonData);
        }
      } catch (error) {
        console.error("❌ Fetch error:", error);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);
  const chapitreOptions = (data?.chapitres || []).map((chapitre) => ({
    label: chapitre.Name,
    value: chapitre.ChapitreID
  }));

  const writableBusLineIDs = new Set(
    (data?.BusLines || [])
      .filter((busLine) => Number(busLine.writeaccessUsers) === 1)
      .map((busLine) => normalizeID(busLine.BusLineID))
      .filter((busLineID) => busLineID !== null)
  );

  const applyUserUpdatesLocally = (userID, updates) => {
    setData((prev) => {
      if (!prev) {
        return prev;
      }

      const chapitreID = Object.prototype.hasOwnProperty.call(updates, "ChapitreID")
        ? normalizeID(updates.ChapitreID)
        : undefined;
      const busLineID = Object.prototype.hasOwnProperty.call(updates, "BusLineID")
        ? normalizeID(updates.BusLineID)
        : undefined;
      const waitList = Object.prototype.hasOwnProperty.call(updates, "WaitList")
        ? normalizeWaitList(updates.WaitList)
        : undefined;

      const chapitre = chapitreID === undefined
        ? null
        : prev.chapitres.find((entry) => entry.ChapitreID === chapitreID);
      const busLine = busLineID === undefined
        ? null
        : prev.BusLines.find((entry) => entry.BusLineID === busLineID);

      return {
        ...prev,
        users: prev.users.map((entry) =>
          entry.UserID === userID
            ? {
              ...entry,
              ...(chapitreID !== undefined ? {
                ChapitreID: chapitreID,
                ChapitreName: chapitre ? chapitre.Name : null
              } : {}),
              ...(busLineID !== undefined ? {
                BusLineID: busLineID,
                BusLineName: busLine ? busLine.Name : null
              } : {}),
              ...(waitList !== undefined ? { WaitList: waitList } : {}),
              ...(Object.prototype.hasOwnProperty.call(updates, "priceAmount") ? { priceAmount: updates.priceAmount } : {}),
              ...(Object.prototype.hasOwnProperty.call(updates, "priceCurrency") ? { priceCurrency: updates.priceCurrency } : {}),
              ...(Object.prototype.hasOwnProperty.call(updates, "priceStr") ? { priceStr: updates.priceStr } : {})
            }
            : entry
        )
      };
    });
  };

  const setUserSavingState = (userID, isSaving) => {
    setSavingUserIDs((prev) => ({ ...prev, [userID]: isSaving }));
    setData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        users: prev.users.map((entry) =>
          entry.UserID === userID
            ? { ...entry, _isSaving: isSaving }
            : entry
        )
      };
    });
  };

  const updateUserAssignments = async (user, updates) => {
    const nextChapitreID = normalizeID(Object.prototype.hasOwnProperty.call(updates, "ChapitreID")
      ? updates.ChapitreID
      : user.ChapitreID);
    const nextBusLineID = normalizeID(Object.prototype.hasOwnProperty.call(updates, "BusLineID")
      ? updates.BusLineID
      : user.BusLineID);
    const nextWaitList = normalizeWaitList(Object.prototype.hasOwnProperty.call(updates, "WaitList")
      ? updates.WaitList
      : user.WaitList);

    const currentChapitreID = normalizeID(user.ChapitreID);
    const currentBusLineID = normalizeID(user.BusLineID);
    const currentWaitList = normalizeWaitList(user.WaitList);

    if (
      nextChapitreID === currentChapitreID &&
      nextBusLineID === currentBusLineID &&
      nextWaitList === currentWaitList
    ) {
      return;
    }

    applyUserUpdatesLocally(user.UserID, {
      ChapitreID: nextChapitreID,
      BusLineID: nextBusLineID,
      WaitList: nextWaitList
    });
    setUserSavingState(user.UserID, true);
    try {
      const response = await fetch("./backend/admin/update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userID: user.UserID,
          ChapitreID: nextChapitreID,
          BusLineID: nextBusLineID,
          WaitList: nextWaitList
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Update failed");
      }
    } catch (error) {
      applyUserUpdatesLocally(user.UserID, {
        ChapitreID: currentChapitreID,
        BusLineID: currentBusLineID,
        WaitList: currentWaitList
      });
      console.error("❌ Update error:", error);
      window.alert(`Update failed: ${error.message}`);
    } finally {
      setUserSavingState(user.UserID, false);
    }
  };

  const updateUserPrice = async (user, priceAmount, priceCurrency) => {
    const normalizedAmount = Number(priceAmount);
    const nextPriceAmount = Number.isInteger(normalizedAmount) ? normalizedAmount : priceAmount;
    const nextPriceCurrency = priceCurrency ?? "";
    const nextPriceStr = `${nextPriceAmount} ${nextPriceCurrency}`;
    const currentPriceAmount = user.priceAmount ?? null;
    const currentPriceCurrency = user.priceCurrency ?? "";
    const currentPriceStr = user.priceStr ?? "";

    if (nextPriceAmount === currentPriceAmount && nextPriceCurrency === currentPriceCurrency) {
      return;
    }

    applyUserUpdatesLocally(user.UserID, {
      priceAmount: nextPriceAmount,
      priceCurrency: nextPriceCurrency,
      priceStr: nextPriceStr
    });
    setUserSavingState(user.UserID, true);
    try {
      const response = await fetch("./backend/admin/update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userID: user.UserID,
          priceAmount: nextPriceAmount,
          priceCurrency: nextPriceCurrency
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "Update failed");
      }
    } catch (error) {
      applyUserUpdatesLocally(user.UserID, {
        priceAmount: currentPriceAmount,
        priceCurrency: currentPriceCurrency,
        priceStr: currentPriceStr
      });
      console.error("❌ Price update error:", error);
      throw error;
    } finally {
      setUserSavingState(user.UserID, false);
    }
  };

  const updateChapitreAttributes = async (chapitre, updates) => {
    if (!chapitre) {
      return;
    }

    const response = await fetch("./backend/admin/updateChapitre.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ChapitreID: chapitre.ChapitreID,
        ...updates
      })
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || "Update failed");
    }

    const updatedChapitre = result.chapitre || {
      ...chapitre,
      ...updates
    };

    setData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        chapitres: prev.chapitres.map((entry) =>
          entry.ChapitreID === updatedChapitre.ChapitreID
            ? { ...entry, ...updatedChapitre }
            : entry
        )
      };
    });

    setChapitreEditTarget(updatedChapitre);
  };

  const chapitreEditorTemplate = (rowData) => {
    const selectedChapitre = normalizeID(rowData.ChapitreID);
    const hasCurrentOption = chapitreOptions.some((option) => option.value === selectedChapitre);
    const chapitreEditorOptions = !hasCurrentOption && selectedChapitre !== null
      ? [
        ...chapitreOptions,
        { label: rowData.ChapitreName, value: selectedChapitre }
      ]
      : chapitreOptions;

    return (
      <Dropdown
        value={selectedChapitre}
        options={chapitreEditorOptions}
        onChange={(e) => updateUserAssignments(rowData, { ChapitreID: e.value })}
        optionLabel="label"
        optionValue="value"
        placeholder="No chapitre"
        disabled={!!rowData._isSaving}
        className="w-15rem"
      />
    );
  };

  const statusEditorTemplate = (rowData) => {
    const statusOptionTemplate = (option) => option ? getUserStatus(option.value) : null;
    const selectedStatus = normalizeWaitList(rowData.WaitList);
    const nextAllowedStatuses = allowedStatusTransitions[selectedStatus] || [];
    const filteredStatusOptions = statusOptions.filter(
      (option) => option.value === selectedStatus || nextAllowedStatuses.includes(option.value)
    );
    const hasCurrentOption = filteredStatusOptions.some((option) => option.value === selectedStatus);
    const statusEditorOptions = hasCurrentOption
      ? filteredStatusOptions
      : [
        ...filteredStatusOptions,
        { label: `Status ${selectedStatus}`, value: selectedStatus }
      ];
    return (
      <Dropdown
        value={selectedStatus}
        options={statusEditorOptions}
        onChange={(e) => updateUserAssignments(rowData, { WaitList: e.value })}
        optionLabel="label"
        optionValue="value"
        itemTemplate={statusOptionTemplate}
        valueTemplate={statusOptionTemplate}
        disabled={!!rowData._isSaving || statusEditorOptions.length <= 1}
        className="w-10rem"
      />
    );
  };

  const columnBodyByField = {
    WaitList: (rowData) => getUserStatus(rowData.WaitList),
    EditChapitre: chapitreEditorTemplate,
    EditWaitList: statusEditorTemplate
  };
  const columnFieldByField = {
    EditChapitre: "ChapitreID",
    EditWaitList: "WaitList"
  };


  if (loading) return <p>Loading data...
    <i className="pi pi-spin pi-spinner ml-2" style={{ fontSize: "1rem" }}></i>
  </p>;
  if (!data) return <p>Unauthorized access
    <i className="pi pi-exclamation-triangle ml-2" style={{ fontSize: "1rem" }}></i>
  </p>;

  return (
    <div className="card">
      <ChapitreDetailsDialog
        activeChapitre={activeChapitre}
        visible={chapitreDialogVisible}
        onHide={() => setChapitreDialogVisible(false)}
        users={data.users}
      />
      <ChapitreEditDialog
        chapitre={chapitreEditTarget}
        visible={chapitreEditVisible}
        onHide={() => setChapitreEditVisible(false)}
        onSave={(updates) => updateChapitreAttributes(chapitreEditTarget, updates)}
      />

      <TabView>
        <TabPanel leftIcon="pi-car pi mr-2" header="Buses">
          <GenericTabView items={data.BusLines} fieldname="BusLineID"
            renderOverview={(item) => <>
              <div className="col-12 md:col-12 flex justify-content-between align-items-center">
                <h2>{item.Name} {countries[item.Country].emoji}</h2>
                <p>
                  &nbsp;<i className="pi pi-envelope"></i>&nbsp;
                  {item.ContactEmail || "Not available"} &nbsp;|&nbsp;
                  <i className="pi pi-phone"></i>&nbsp;
                  {item.ContactPhone || "Not available"}</p>
              </div>
              <div className="col-12 md:col-6">
                {OccupancyRate(
                  item.MaxSeats,
                  data.users.filter(user => user.BusLineID === item.BusLineID && isConfirmedLike(user.WaitList) && ["oneway", "roundtrip"].includes(user.TripType)).length,
                  data.users.filter(user => user.BusLineID === item.BusLineID && isWaiting(user.WaitList) && ["oneway", "roundtrip"].includes(user.TripType)).length,
                  "Occupancy Rate to Paris",
                  formlabels['en'].almostFullWarning
                )}
              </div>
              <div className="col-12 md:col-6">
                {OccupancyRate(
                  item.MaxSeats,
                  data.users.filter(user => user.BusLineID === item.BusLineID && isConfirmedLike(user.WaitList) && ["return", "roundtrip"].includes(user.TripType)).length,
                  data.users.filter(user => user.BusLineID === item.BusLineID && isWaiting(user.WaitList) && ["return", "roundtrip"].includes(user.TripType)).length,
                  "Occupancy Rate from Chartres",
                  formlabels['en'].almostFullWarning
                )}
              </div></>}
            data={data}
            canUpdatePrice={(user) => writableBusLineIDs.has(normalizeID(user.BusLineID))}
            onUpdatePrice={updateUserPrice}
            isColumnSortable={(col) => !["EditChapitre"].includes(col.field)}
            getColumnField={(col) => columnFieldByField[col.field] || col.field}
            getColumnBody={(col) => columnBodyByField[col.field] || null}
          />
        </TabPanel>
        <TabPanel leftIcon="pi-users pi mr-2" header="Chapitres">
          <GenericTabView items={data.chapitres} fieldname="ChapitreID"
            renderOverview={(item) => <>
              <div className="col-12 md:col-12">
                <div className="flex justify-content-between align-items-center">
                  <h2 className="m-0">{item.Name}</h2>
                  <Button
                    icon="pi pi-cog"
                    className="p-button-text"
                    aria-label="Configure chapitre"
                    data-cy="chapitre-config-button"
                    onClick={() => {
                      setChapitreEditTarget(item);
                      setChapitreEditVisible(true);
                    }}
                  />
                </div>

                <p>
                  {item.BusLines.map((bus, index) => { return bus.Name + " " + countries[bus.Country].emoji + ""; }).join(" — ")}
                </p>
              </div>
              <div className="col-12 md:col-12">
                <div className="flex align-items-center" onClick={() => {
                  setActiveChapitre(item);
                  setChapitreDialogVisible(true);
                }} style={{ cursor: "pointer" }}>
                  {/*<div className="flex-grow-1" >*/}
                  {item.MaxPeople && OccupancyRate(item.MaxPeople,
                    data.users.filter(user => user.ChapitreID === item.ChapitreID && isConfirmedLike(user.WaitList)).length,
                    data.users.filter(user => user.ChapitreID === item.ChapitreID && isWaiting(user.WaitList)).length,
                    "Occupancy Rate",
                    formlabels['en'].almostFullWarning)}
                  {/*</div>
                  <div className="flex align-items-center justify-content-center mr-2" style={{ minWidth: "2.5rem" }}>
                    <Button icon="pi pi-plus-circle" aria-label="Filter"
                      rounded text
                       />
                  </div>*/}
                </div>
              </div></>}
            data={data}
            canUpdatePrice={(user) => writableBusLineIDs.has(normalizeID(user.BusLineID))}
            onUpdatePrice={updateUserPrice}
            isColumnSortable={(col) => !["EditChapitre", "EditWaitList"].includes(col.field)}
            getColumnField={(col) => columnFieldByField[col.field] || col.field}
            getColumnBody={(col) => columnBodyByField[col.field] || null}
          />
        </TabPanel>
        <TabPanel leftIcon="pi-address-book pi mr-2" header="Users">
          <div className="grid ui-fluid">
            <UserListTable
              users={data.users || []}
              title={`Participants: (${(data.users || []).length})`}
              emptyMessage="No users available."
              downloadFilename="users_accessible_for_admin"
              canUpdatePrice={(user) => writableBusLineIDs.has(normalizeID(user.BusLineID))}
              onUpdatePrice={updateUserPrice}
              isColumnSortable={(col) => !["EditChapitre", "EditWaitList"].includes(col.field)}
              getColumnField={(col) => columnFieldByField[col.field] || col.field}
              getColumnBody={(col) => columnBodyByField[col.field] || null}
            />
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default AdminDashboard;
